import { interval as observableInterval, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import { Component, Input, Inject, Output, EventEmitter, OnDestroy } from '@angular/core';
import { NgxSpinnerService } from 'ngx-spinner';
import { SessionService } from '../session/session.service';
import { AuthService, IAuthResponse } from '../auth/auth.service';
import { NgbModalRef, NgbModalOptions, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgbdBankIdModal } from '../modal/bank-id.modal.component';
import { DOCUMENT } from '@angular/common';
import { ErrorModalService } from '../modal/error.modal.service';
import { FiliDb } from '../interfaces/filiDb.interface';
import { Trad } from '../interfaces/trad.interface';
import { DeviceDetectorService } from 'ngx-device-detector';
import { NgbdOtpSignModal } from '../modal/otp-sign.modal.component';
import { TranslateService } from '@ngx-translate/core'; 

@Component({
  selector: 'sign',
  template: `<button (click)="sign()" [disabled]="!(files && files.length)" class="btn" [ngClass]="btnClass || 'btn-garanti btn-blue'" [attr.title]="'label.signera' | translate"><fa-icon *ngFor="let file of files; last as isLast" [icon]="['fas', 'pen-nib']" [ngClass]="isLast ? 'mr-2' : 'mr-1'" [fixedWidth]="true"></fa-icon>{{ 'label.signera' | translate }}</button>`,
  styles: [`
  .btn.active.focus, .btn.active:focus, .btn.focus, .btn:active.focus, .btn:active:focus, .btn:focus {
     outline: 0;
     outline-offset: 0;
   }
  .btn-link {
    padding: 0;
  }`]
})

export class SignComponent implements OnDestroy {
  @Input()
  public files: FiliDb[];
  @Input()
  public trad?: Trad;
  @Input()
  public caption: string;
  @Input()
  public btnClass: any;
  @Output()
  public onSuccess: EventEmitter<any> = new EventEmitter();

  constructor(
    protected translate: TranslateService,
    protected session: SessionService,
    protected auth: AuthService,
    protected modalService: NgbModal,
    protected errorModalService: ErrorModalService,
    protected spinner: NgxSpinnerService,
    protected deviceDetector: DeviceDetectorService,
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject('BANKID_INTERVAL') protected bankIdInterval: number) {
  }

  private orderRef: string;
  private lockedWindow: NgbModalRef;
  private collectionLoop: Subscription;
  private ongoingTransaction: boolean;

  private qrExpiresIn = 300;
  private qrRemaining: number;
  private qrCountdownInterval: any;

  public sign() {
    const loginMethod = this.session.getLoginMethod();
    if (loginMethod === 'otp')
      this.signOtp();
    else if (loginMethod.startsWith('mobile-'))
      this.signMobileBankId(loginMethod);
    else
      this.signBankIdOnFile();
  }

  private signOtp(): void {
    this.spinner.show();
    this.auth.initSignOtp(this.session.activeInstance.id, this.files.map(f => f.id), this.session.getLanguage()).subscribe(
      (request) => {
        this.spinner.hide();
        this.openOtpSignModal();
        this.lockedWindow.componentInstance.requestId = request.requestId;
        this.lockedWindow.componentInstance.filesToSign = request.filesToSign;
        this.lockedWindow.componentInstance.recipient = request.recipient;
        this.lockedWindow.result.then(
          (result) => {
            if (!result) return;
            this.spinner.show();
            this.auth.signOtp(this.session.activeInstance.id, result.request, result.otp).subscribe(
              () => {
                this.spinner.hide();
                if (this.onSuccess) this.onSuccess.emit();
              },
              (exception) => {
                this.spinner.hide();
                this.openErrorModal('Kunde inte signera', exception.error.message);
              });
          });
      },
      (exception) => {
        this.spinner.hide();
        this.openErrorModal('Kunde inte signera', exception.error.message);
      })
  }

  private signBankIdOnFile(): void {
    this.spinner.show();
    this.auth.signBankId(this.session.activeInstance.id, this.files.map(f => f.id), this.trad ? this.trad.id : null).subscribe(
      (authResponse: IAuthResponse) => {
        this.spinner.hide();
        this.openBankIdModal();
        this.lockedWindow.componentInstance.isMobileBankId = false;
        this.lockedWindow.componentInstance.autostartToken = authResponse.autoStartToken;
        this.ongoingTransaction = true;
        this.startBankIdClient(authResponse.autoStartToken);
        this.collectLoop(); // starta collectloopen
      },
      (exception) => {
        this.spinner.hide();
        if (exception.status == 400) {   
          const errorKey = "SIGNING_ERROR";
          this.translate.get(['bankid.could_not_sign', 'bankid.' + errorKey.toLowerCase()]).subscribe(translations => {
            const title = translations['bankid.could_not_sign'];
            const message = translations['bankid.' + errorKey.toLowerCase()];
            this.openErrorModal(title, message);
          });
        }
        else {
          this.openErrorModal("Kunde inte signera", exception.error.exceptionMessage);
        }
      });
  }

  public signMobileBankId(loginMethod: string) {
    this.spinner.show();
    this.openBankIdModal();

    if (this.lockedWindow && this.lockedWindow.componentInstance) {
      this.lockedWindow.componentInstance.isMobileBankId = true;
      this.lockedWindow.componentInstance.showQrCode = !this.deviceDetector.isMobile();
    }

    this.startTotalCountdown();
    this.startNewBankIdSession();

    this.spinner.hide();
  }

  private collectLoop() {
    let lastCollectHandled = true;
    this.collectionLoop = observableInterval(this.bankIdInterval).pipe(
      takeWhile(() => this.ongoingTransaction)).subscribe(
        () => {
          if (!lastCollectHandled)
            return;

          lastCollectHandled = false;

          this.auth.collectBankId(this.orderRef).subscribe(
            collectResponse => {
              if (collectResponse.status === 'failed' && collectResponse.timedOut == true && this.qrRemaining > 0) {
                this.unsubscribeCollectionLoop();
                this.startNewBankIdSession();
                return;
              }

              lastCollectHandled = true;
              if (this.lockedWindow && this.lockedWindow.componentInstance) {
                this.lockedWindow.componentInstance.hintCode = collectResponse.hintCode;
                this.lockedWindow.componentInstance.status = collectResponse.status;
                if (this.lockedWindow.componentInstance.isMobileBankId && collectResponse.qrCode) {
                  this.lockedWindow.componentInstance.qrCode = collectResponse.qrCode;
                }
              }

              if (collectResponse.status === 'complete') {
                if (this.onSuccess)
                  this.onSuccess.emit();
                this.lockedWindow.close();
              }

              this.ongoingTransaction = collectResponse.status === 'pending';
            },
            error => {
              this.ongoingTransaction = false;
              this.unsubscribeCollectionLoop();
              this.openErrorModal('Kunde inte signera', error.error.exceptionMessage);
            });
        },
        () => this.ongoingTransaction = false);
  }

  public openModal(content, windowClass?: NgbModalOptions) {
    if (this.lockedWindow) {
      this.lockedWindow.close();
    }

    this.lockedWindow = this.modalService.open(content, windowClass);

    this.lockedWindow.result.then(() => {
      this.cancelBankId();
    });
  }

  private openOtpSignModal() {
    this.openModal(NgbdOtpSignModal, { backdrop: 'static', keyboard: false, centered: true });
  }

  private openBankIdModal() {
    this.openModal(NgbdBankIdModal, { backdrop: 'static', keyboard: false, centered: true });
    if (this.lockedWindow && this.lockedWindow.componentInstance) {
      this.lockedWindow.componentInstance.qrRemaining = this.qrRemaining;
      this.lockedWindow.componentInstance.extendTimeoutClicked.subscribe(() => {
        this.extendQrCode();
      });
    }
  }

  public extendQrCode() {
    this.qrRemaining = this.qrExpiresIn;
  }

  private startTotalCountdown() {
    this.qrRemaining = this.qrExpiresIn;
    if (this.qrCountdownInterval) {
      clearInterval(this.qrCountdownInterval);
    }

    this.qrCountdownInterval = setInterval(() => {
      this.qrRemaining--;
      if (this.lockedWindow && this.lockedWindow.componentInstance) {
        this.lockedWindow.componentInstance.qrRemaining = this.qrRemaining;
      }

      if (this.qrRemaining <= 0) {
        clearInterval(this.qrCountdownInterval);

        if (this.lockedWindow && this.lockedWindow.componentInstance) {
          this.lockedWindow.componentInstance.hintCode = 'expiredTransaction';
          this.lockedWindow.componentInstance.status = 'failed';
          this.lockedWindow.componentInstance.showBankIdLink = true;
          this.ongoingTransaction = false;
          this.unsubscribeCollectionLoop();
        }
      }
    }, 1000);
  }

  private startNewBankIdSession() {
    this.auth.signBankId(this.session.activeInstance.id, this.files.map(f => f.id), this.trad ? this.trad.id : null).subscribe(
      authResponse => {
        this.orderRef = authResponse.orderRef;
        this.unsubscribeCollectionLoop();
        this.ongoingTransaction = true;
        this.collectLoop();

        if (this.deviceDetector.isMobile() && this.lockedWindow && this.lockedWindow.componentInstance)
          this.startBankIdClient(authResponse.autoStartToken);
      },
      error => {
        this.openErrorModal("Signeringsfel", error.error.exceptionMessage);
        if (this.lockedWindow) {
          this.lockedWindow.close();
        }
      }
    );
  }

  private unsubscribeCollectionLoop() {
    if (!!this.collectionLoop && !this.collectionLoop.closed) {
      this.collectionLoop.unsubscribe();
    }
  }

  private startBankIdClient(token: string, returnUrl: string = null) {
    this.document.location.href = `bankid:///?autostarttoken=${token}&redirect=${returnUrl ? encodeURIComponent(returnUrl) : ''}`;
  }

  public openErrorModal(title: string, message: string) {
    this.errorModalService.open(title, message, () => { }, this.lockedWindow);
  }

  private cancelBankId() {
    if (this.ongoingTransaction) {
      this.auth.cancelBankId(this.orderRef).subscribe(() => this.unsubscribeCollectionLoop());
    } else {
      this.unsubscribeCollectionLoop();
    }
    if (this.qrCountdownInterval) {
      clearInterval(this.qrCountdownInterval);
      this.qrCountdownInterval = null;
    }
  }

  ngOnDestroy() {
    this.cancelBankId();
  }
}
